import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AffiliateService } from '../../core/services/affiliate.service';

@Component({
  selector: 'app-affiliates-login',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <main class="min-h-screen bg-stone-900 text-stone-100 py-16 px-4 sm:px-6 lg:px-8">
      <div class="max-w-md mx-auto">
        <!-- Header / Logo -->
        <div class="text-center mb-10">
          <span class="inline-block p-3 rounded-full bg-stone-800 text-amber-400 mb-4 border border-stone-700">
            <span class="material-symbols-outlined text-3xl">key</span>
          </span>
          <h1 class="text-3xl font-serif font-bold tracking-tight text-white">Partner Portal Login</h1>
          <p class="mt-2 text-sm text-stone-400">Access your referral metrics, conversion ledger, and Stripe payouts.</p>
        </div>

        <!-- Form Card -->
        <div class="bg-stone-800/80 backdrop-blur border border-stone-700/60 rounded-2xl p-8 shadow-2xl">
          @if (errorMessage()) {
            <div class="mb-6 p-4 rounded-lg bg-red-950/60 border border-red-800/60 text-red-300 text-sm flex items-start gap-3">
              <span class="material-symbols-outlined text-red-400 text-xl shrink-0">error</span>
              <span>{{ errorMessage() }}</span>
            </div>
          }

          <form (ngSubmit)="onLogin()" class="space-y-5">
            <div>
              <label class="block text-xs font-semibold uppercase tracking-wider text-stone-300 mb-2">Email Address</label>
              <input
                type="email"
                [(ngModel)]="email"
                name="email"
                required
                placeholder="partner@example.com"
                class="w-full bg-stone-900 border border-stone-700 rounded-lg px-4 py-3.5 text-stone-100 placeholder-stone-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition-all text-sm"
              />
            </div>

            <div>
              <label class="block text-xs font-semibold uppercase tracking-wider text-stone-300 mb-2">Password</label>
              <input
                type="password"
                [(ngModel)]="password"
                name="password"
                required
                placeholder="••••••••"
                class="w-full bg-stone-900 border border-stone-700 rounded-lg px-4 py-3.5 text-stone-100 placeholder-stone-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition-all text-sm"
              />
            </div>

            <button
              type="submit"
              [disabled]="affiliateService.isLoading()"
              class="w-full mt-4 bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold uppercase tracking-widest py-4 px-6 rounded-lg shadow-lg hover:shadow-amber-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
            >
              @if (affiliateService.isLoading()) {
                <span class="material-symbols-outlined animate-spin text-base">sync</span>
                Signing In...
              } @else {
                <span>Sign In to Dashboard</span>
                <span class="material-symbols-outlined text-base">login</span>
              }
            </button>
          </form>

          <div class="mt-8 pt-6 border-t border-stone-700/60 text-center">
            <p class="text-xs text-stone-400">
              Not a partner yet?
              <a routerLink="/affiliates/join" class="text-amber-400 hover:text-amber-300 font-semibold ml-1 underline">
                Apply for Partner Program
              </a>
            </p>
          </div>
        </div>
      </div>
    </main>
  `,
})
export class AffiliatesLoginComponent {
  protected readonly affiliateService = inject(AffiliateService);
  private readonly router = inject(Router);

  email = '';
  password = '';
  errorMessage = signal<string | null>(null);

  onLogin(): void {
    this.errorMessage.set(null);
    this.affiliateService.login({
      email: this.email,
      password: this.password,
    }).subscribe({
      next: () => {
        this.router.navigate(['/affiliates/dashboard']);
      },
      error: (err) => {
        this.errorMessage.set(err.message || 'Invalid credentials');
      },
    });
  }
}
